import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Camera, Loader2, CheckCircle2, BadgeCheck } from "lucide-react";

const LANGUAGES = [
  { k: "fr", l: "Français" },
  { k: "en", l: "English" },
  { k: "br", l: "Brezhoneg" },
  { k: "es", l: "Español" },
  { k: "de", l: "Deutsch" },
  { k: "it", l: "Italiano" },
  { k: "nl", l: "Nederlands" },
  { k: "pt", l: "Português" },
];

const HOST_TYPES = [
  { v: "particulier", l: "Particulier" },
  { v: "conciergerie", l: "Conciergerie" },
  { v: "professionnel", l: "Professionnel" },
];

const resolvePhoto = (p) => p?.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;

export default function HostProfilePanel() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    first_name: "", last_name: "", age: "", languages: ["fr"],
    hosting_since: "", host_type: "particulier", description: "",
    avatar: null, phone: "",
  });
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    if (!user) return;
    const hp = user.host_profile || {};
    setForm({
      first_name: hp.first_name || "",
      last_name: hp.last_name || "",
      age: hp.age || "",
      languages: hp.languages?.length ? hp.languages : ["fr"],
      hosting_since: hp.hosting_since || String(new Date().getFullYear()),
      host_type: hp.host_type || "particulier",
      description: hp.description || "",
      avatar: hp.avatar || user.avatar || null,
      phone: hp.phone || "",
    });
    api.get(`/users/${user.id}/public-profile`)
      .then(({ data }) => setStats(data.stats))
      .catch(() => {});
  }, [user]);

  const toggleLang = (k) => {
    setForm((p) => ({
      ...p,
      languages: p.languages.includes(k) ? p.languages.filter((x) => x !== k) : [...p.languages, k],
    }));
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, avatar: data.path }));
      toast.success("Photo téléchargée");
    } catch (err) {
      toast.error("Échec de l'upload");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: form.age ? Number(form.age) : null,
      };
      const { data } = await api.put("/me/host-profile", payload);
      setUser?.(data);
      toast.success("Profil hôte mis à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">Profil hôte</div>
      <h1 className="font-display text-4xl font-bold tracking-tight mb-2">Votre identité d'hôte</h1>
      <p className="text-neutral-600 mb-8 max-w-2xl">
        Un profil complet inspire confiance et augmente significativement le taux de réservation.
      </p>

      {/* Verified badges + stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <div className="border border-black/10 p-4 bg-white" data-testid="verif-email">
          <div className="flex items-center gap-2">
            {user?.email_verified ? <CheckCircle2 className="w-4 h-4 text-[#166534]" /> : <CheckCircle2 className="w-4 h-4 text-neutral-300" />}
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold">Email</div>
          </div>
          <div className="text-sm mt-1">{user?.email_verified ? "Vérifié" : "Non vérifié"}</div>
        </div>
        <div className="border border-black/10 p-4 bg-white" data-testid="verif-phone">
          <div className="flex items-center gap-2">
            {user?.phone_verified ? <CheckCircle2 className="w-4 h-4 text-[#166534]" /> : <CheckCircle2 className="w-4 h-4 text-neutral-300" />}
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold">Téléphone</div>
          </div>
          <div className="text-sm mt-1">{user?.phone_verified ? "Vérifié" : "À vérifier"}</div>
        </div>
        <div className="border border-black/10 p-4 bg-white">
          <div className="flex items-center gap-2">
            <BadgeCheck className={`w-4 h-4 ${stats?.superhost ? "text-[#7A1535]" : "text-neutral-300"}`} />
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold">Super hôte</div>
          </div>
          <div className="text-sm mt-1">{stats?.superhost ? "Acquis" : "À débloquer"}</div>
        </div>
        <div className="border border-black/10 p-4 bg-white">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold">Avis · Note</div>
          <div className="text-sm mt-1">
            {stats?.review_count || 0} avis
            {stats?.avg_rating ? ` · ${stats.avg_rating}★` : ""}
          </div>
        </div>
      </div>

      <form onSubmit={save} className="border border-black/10 bg-white p-6 space-y-6">
        {/* Photo */}
        <div className="flex items-start gap-5">
          <div className="relative">
            <div className="w-24 h-24 bg-[#F5F2EC] overflow-hidden border border-black/10">
              {form.avatar ? (
                <img src={resolvePhoto(form.avatar)} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-2xl text-neutral-400 font-display">
                  {(form.first_name || user?.name || "?")[0]?.toUpperCase()}
                </div>
              )}
            </div>
            <button
              type="button"
              data-testid="host-photo-upload"
              onClick={() => fileInput.current?.click()}
              className="absolute -bottom-2 -right-2 w-9 h-9 bg-[#0A0A0A] text-white flex items-center justify-center hover:bg-[#1B2A49]"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            </button>
            <input ref={fileInput} type="file" accept="image/*" onChange={onPhoto} className="hidden" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Photo de profil</div>
            <p className="text-sm text-neutral-600 max-w-md">Une photo nette et chaleureuse — un visage augmente la confiance des voyageurs.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Prénom</div>
            <input data-testid="hp-first_name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Nom (optionnel)</div>
            <input data-testid="hp-last_name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Âge (optionnel)</div>
            <input data-testid="hp-age" type="number" min={18} max={120} value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Depuis quand héberge</div>
            <input data-testid="hp-hosting_since" placeholder="2024" value={form.hosting_since} onChange={(e) => setForm({ ...form, hosting_since: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Statut</div>
            <select data-testid="hp-host_type" value={form.host_type} onChange={(e) => setForm({ ...form, host_type: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A] bg-white">
              {HOST_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Téléphone</div>
            <input data-testid="hp-phone" type="tel" placeholder="+33 6 12 34 56 78" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
          </label>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Langues parlées</div>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map((L) => (
              <button
                key={L.k}
                type="button"
                data-testid={`hp-lang-${L.k}`}
                onClick={() => toggleLang(L.k)}
                className={`text-xs uppercase tracking-[0.1em] font-bold px-3 py-2 border ${
                  form.languages.includes(L.k)
                    ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                    : "bg-white border-black/15"
                }`}
              >
                {L.l}
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Description</div>
          <textarea data-testid="hp-description" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Présentez-vous : votre histoire avec la Bretagne, ce que vous aimez partager…" className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
        </label>

        {/* Live host metrics */}
        <div className="grid grid-cols-2 gap-3 p-4 bg-[#FAFAFA] border border-black/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Temps de réponse</div>
            <div className="font-display text-lg font-bold mt-1">{stats?.response_time || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Taux de réponse</div>
            <div className="font-display text-lg font-bold mt-1">{stats?.response_rate != null ? `${stats.response_rate}%` : "—"}</div>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="submit" data-testid="hp-save" disabled={saving} className="bg-[#0A0A0A] text-white px-6 py-3 font-semibold hover:bg-[#1B2A49] disabled:opacity-60">
            {saving ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Enregistrement…</span> : "Enregistrer le profil"}
          </button>
        </div>
      </form>
    </div>
  );
}
